import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import TranslateIcon from '@mui/icons-material/Translate'
import MinimizeIcon from '@mui/icons-material/Minimize'
import CloseIcon from '@mui/icons-material/Close'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useTranslatorWidget } from './TranslatorProvider'
import { MAX_TEXT_LENGTH } from './useTranslator'
import usePointerDrag, { placementStyle } from './usePointerDrag'

const WIDGET_WIDTH_PX = 416 // ~26 rem
const WIDGET_MARGIN_PX = 24
/**
 * Above MIC's modal dialogs (MUI modal 1300) and below snackbars/tooltips (1400/1500): the main
 * use of the widget is translating what a dialog or a table shows, so it must stay reachable
 * while one is open. Focus/aria-hidden while a Dialog is open are handled by rendering the widget
 * inside the dialog's container (TranslatorWidget + useDialogPortalTarget).
 */
const WIDGET_Z_INDEX = 1350

/**
 * Floating, non-modal translator panel (#2892). A pure view of TranslatorProvider: it owns no
 * translation state, so unmounting it (minimize) loses nothing (RN3.1).
 */
const TranslatorPanel = () => {
  const { t_ } = useSmartTranslation()
  const { ui, minimize, close, setPosition, widgetRef, translator } = useTranslatorWidget()
  // `bounds` as a getter: `offsetHeight` is 0 on first render (the ref is still unset) and must be
  // re-read fresh at pointerdown time and on every resize re-clamp, not memoised from that render.
  const { handleProps } = usePointerDrag({
    position: ui.position,
    onMove: setPosition,
    bounds: () => ({ width: WIDGET_WIDTH_PX, height: widgetRef.current ? widgetRef.current.offsetHeight : 0 })
  })

  const {
    languages, sourceLanguage, targetLanguage, text, translating, translation, translationFailed,
    unavailable, canTranslate, overLimit, setText, setSourceLanguage, setTargetLanguage, translate, cancel
  } = translator

  // RN8/V3: source and target must differ — each dropdown stops offering the other one's choice.
  const languageSelect = (id, label, value, onChange, excluded) => (
    <FormControl fullWidth size='small'>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // The dropdown is a MUI Popover at the modal layer (1300); it must open above the panel.
        MenuProps={{ sx: { zIndex: WIDGET_Z_INDEX + 1 } }}
      >
        {languages.filter((language) => language.code !== excluded).map((language) => (
          <MenuItem key={language.code} value={language.code}>
            {language.nativeName ?? language.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Paper
      ref={widgetRef}
      role='dialog'
      aria-modal='false'
      aria-label={t_('Translator')}
      elevation={8}
      sx={{
        position: 'fixed',
        zIndex: WIDGET_Z_INDEX,
        width: WIDGET_WIDTH_PX,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: (theme) => theme.palette.background.color_373636,
        borderRadius: 2
      }}
      style={placementStyle(ui.position, WIDGET_MARGIN_PX)}
    >
      {/*
        Header = drag handle. Its buttons stop pointerdown so clicking them never starts a drag.
        Only onPointerDown/style are spread here (not onClickCapture): that guard exists to
        swallow the spurious click a drag-then-release generates on the handle itself (see the
        bubble's Fab, which is its own click target) — spreading it here would also swallow real
        clicks on the nested Minimize/Close buttons after any drag, since it runs on capture.
      */}
      <Stack
        direction='row'
        role='toolbar'
        alignItems='center'
        spacing={1}
        aria-label={t_('Drag to move')}
        onPointerDown={handleProps.onPointerDown}
        // userSelect none: dragging the header must not highlight the title.
        sx={{ paddingX: 2, paddingY: 1, userSelect: 'none', ...handleProps.style }}
      >
        <TranslateIcon color='primary' fontSize='small' />
        <Typography variant='subtitle1' color='primary' sx={{ flexGrow: 1, fontWeight: 600 }}>
          {t_('Translator')}
        </Typography>
        <Stack direction='row' onPointerDown={(event) => event.stopPropagation()}>
          {/* size='large' → 48 px targets: usable from MOS / a tablet, not only with a mouse. */}
          <IconButton size='large' aria-label={t_('Minimize')} onClick={minimize}>
            <MinimizeIcon />
          </IconButton>
          <IconButton size='large' aria-label={t_('Close')} onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ paddingX: 2, paddingBottom: 1, overflowY: 'auto', flexGrow: 1 }}>
        {unavailable
          ? <Alert severity='warning' sx={{ marginBottom: 2 }}>{t_('The translation service is not available')}</Alert>
          : null}
        <Stack direction='row' spacing={1} sx={{ paddingTop: 1 }}>
          {languageSelect('translator-source-language', t_('Source language'), sourceLanguage, setSourceLanguage, targetLanguage)}
          {languageSelect('translator-target-language', t_('Target language'), targetLanguage, setTargetLanguage, sourceLanguage)}
        </Stack>
        <TextField
          sx={{ marginTop: 2 }}
          label={t_('Text to translate')}
          value={text}
          onChange={(event) => setText(event.target.value)}
          multiline
          minRows={4}
          fullWidth
          size='small'
          error={overLimit}
          helperText={`${text.length} / ${MAX_TEXT_LENGTH}`}
        />
        {translationFailed
          ? <Alert severity='error' sx={{ marginTop: 1 }}>{t_('The translation is not available right now')}</Alert>
          : null}
        {translating
          ? (
            <Stack role='status' direction='row' spacing={1} alignItems='center' sx={{ marginTop: 1 }}>
              <CircularProgress size={18} color='primary' />
              <Typography variant='body2' sx={{ flexGrow: 1 }}>{t_('Translating…')}</Typography>
              <Button size='small' onClick={cancel}>{t_('Cancel')}</Button>
            </Stack>
            )
          : null}
        {translation !== ''
          ? (
            <Paper
              component='section'
              variant='outlined'
              aria-label={t_('Translation')}
              // RN5/CA3: machine origin carried by BOTH a textual notice and a distinct grey panel.
              sx={{
                marginTop: 1,
                padding: 1.5,
                backgroundColor: 'action.hover',
                borderLeft: 4,
                borderLeftColor: 'text.secondary'
              }}
            >
              <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>{translation}</Typography>
            </Paper>
            )
          : null}
      </Box>

      <Stack direction='row' justifyContent='space-between' sx={{ padding: 2, paddingTop: 1 }}>
        <Button variant='outlined' onClick={close}>{t_('Close')}</Button>
        <Button variant='contained' disabled={!canTranslate || translating} onClick={translate}>
          {t_('Translate')}
        </Button>
      </Stack>
    </Paper>
  )
}

export default TranslatorPanel
